import type { UseInViewWithDwellOptions } from '@notino/react-web-utils';
import {
  GetProductDetailAdsQuery,
  Maybe,
} from '@notino/shared/definitions/types';
import {
  UnrenderedReason,
  type AdProvider,
  type RenderedProduct,
  type TSponsoredAdsTrackingRequest,
  type UnrenderedProduct,
} from '@notino/web-tracking';

import { NewRelicBrowser } from '@lib/newRelic/NewRelicBrowser';

export const RETAIL_MEDIA_REPORT_URL = '/api/retail-media/report';

type ProductCarousel = NonNullable<
  NonNullable<GetProductDetailAdsQuery['ProductDetailAds']>['productCarousel']
>;

type CarouselItem = NonNullable<NonNullable<ProductCarousel['items']>[number]>;
type CarouselProduct = NonNullable<
  NonNullable<CarouselItem['products']>[number]
>;

export type SponsoredAdsVpProduct = NonNullable<CarouselProduct['vpProduct']>;

/** One rendered product occurrence with its slot position and beacon payload URLs. */
export interface SponsoredAdsOccurrence {
  productId: string;
  slotId: number;
  vpProduct: Maybe<SponsoredAdsVpProduct>;
  onViewPayload?: Maybe<string>;
  onClickPayload?: Maybe<string>;
  onBasketChangePayload?: Maybe<string>;
  onWishlistPayload?: Maybe<string>;
}

/**
 * Provider is echoed back untouched in every tracking event. Default to `Criteo`
 * since the product-detail placement is Criteo-first; unknown strings pass through.
 */
export const normalizeProvider = (provider?: Maybe<string>): AdProvider =>
  (provider ?? 'Criteo') as AdProvider;

/**
 * Provider-specific viewability rule for the shared `useInViewWithDwell` hook.
 * Kept out of the hook so it stays provider-agnostic. Criteo counts an impression
 * only after the element stays ≥50% visible for a 1000ms dwell.
 */
export const getSponsoredPlacementInViewOptions = (
  provider: AdProvider
): UseInViewWithDwellOptions => {
  if (provider === 'Criteo') {
    return { threshold: 0.5, dwellMs: 1000 };
  }

  // Martailer / default keeps the historical central-viewport rule, no dwell.
  return { rootMargin: '-20% 0px -20% 0px' };
};

/**
 * Flatten `productCarousel.items[].products[]` into an ordered list of occurrences.
 * Deduplication is intentionally NOT applied — Criteo does not support it, so the
 * same product may legitimately appear more than once.
 */
export const flattenCarouselProducts = (
  carousel?: Maybe<ProductCarousel>
): SponsoredAdsOccurrence[] => {
  const occurrences: SponsoredAdsOccurrence[] = [];
  let slotId = 0;

  for (const item of carousel?.items ?? []) {
    for (const product of item?.products ?? []) {
      if (!product?.productId) {
        continue;
      }
      occurrences.push({
        productId: String(product.productId),
        slotId,
        vpProduct: product.vpProduct,
        onViewPayload: product.payloads?.onViewPayload,
        onClickPayload: product.payloads?.onClickPayload,
        onBasketChangePayload: product.payloads?.onBasketChangePayload,
        onWishlistPayload: product.payloads?.onWishlistPayload,
      });
      slotId += 1;
    }
  }

  return occurrences;
};

/** Placement-level payloads (onLoad/onView) from the first carousel item. */
export const getPlacementPayloads = (carousel?: Maybe<ProductCarousel>) =>
  carousel?.items?.find((item) => item?.payloads)?.payloads ?? null;

/**
 * Split the requested occurrences into what actually rendered vs. what did not,
 * for the `PlacementOnLoad` event. A product is "rendered" when the gateway resolved
 * it to a loadable catalog product (`vpProduct`); occurrences the ad provider
 * returned but whose product could not be loaded (`vpProduct` is `null`) are
 * reported as unrendered so the provider can adjust future delivery.
 */
export const partitionOccurrencesByRender = (
  occurrences: SponsoredAdsOccurrence[]
): {
  renderedProducts: RenderedProduct[];
  unrenderedProducts: UnrenderedProduct[];
} => {
  const renderedProducts: RenderedProduct[] = [];
  const unrenderedProducts: UnrenderedProduct[] = [];

  for (const occurrence of occurrences) {
    if (occurrence.vpProduct) {
      renderedProducts.push({
        productId: Number(occurrence.productId),
        slotId: occurrence.slotId,
      });
    } else {
      unrenderedProducts.push({
        productId: Number(occurrence.productId),
        reasonCode: UnrenderedReason.GenericError,
      });
    }
  }

  return { renderedProducts, unrenderedProducts };
};

/**
 * Fire sponsored-ads tracking events to the Retail Media report endpoint via the
 * Beacon API. No-op on the server or when Beacon is unavailable. Tracking must
 * never break rendering, so failures are swallowed.
 */
export const sendSponsoredAdsReport = (
  events: TSponsoredAdsTrackingRequest
): void => {
  if (!events.requests.length) {
    return;
  }

  if (
    typeof navigator === 'undefined' ||
    typeof navigator.sendBeacon !== 'function'
  ) {
    // eslint-disable-next-line no-console
    console.log('[RMA tracking] beacon unavailable (SSR / unsupported)');
    return;
  }

  try {
    const blob = new Blob([JSON.stringify(events)], {
      type: 'application/json',
    });
    navigator.sendBeacon(RETAIL_MEDIA_REPORT_URL, blob);
  } catch (error) {
    NewRelicBrowser.agent.noticeError(
      '[RMA tracking] beacon failed: ' + (error as Error).message
    );
  }
};
